import {
    IconAlertTriangle,
    IconCheck,
    IconDownload,
    IconPencil,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { catFromPath } from '../../toolCatalog';
import CompressResultCard from '../pdf/CompressResultCard';
import { ToolResultBarContext } from './toolResultBar';
import Hint from '../../../../../components/common/Hint';

const PREVIEW_BADGE_STYLES = {
    empty: 'text-[#A8A099] bg-[#F7F5F3] dark:text-zinc-400 dark:bg-zinc-800',
    ready: 'text-pdf-cat-solid bg-pdf-cat-tint dark:text-orange-400 dark:bg-orange-950/30',
    processing: 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-primary-950/30',
    success: 'text-[#1E9E5E] bg-[#E3F6EC] dark:text-emerald-400 dark:bg-emerald-950/30',
    error: 'text-[#D33A2C] bg-pdf-cat-tint dark:text-red-400 dark:bg-red-950/30',
};

function PreviewStatusBadge({ status, label }) {
    return (
        <span className={`rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${PREVIEW_BADGE_STYLES[status] || PREVIEW_BADGE_STYLES.empty}`}>
            {label}
        </span>
    );
}

function PreviewIdle({ title, hint, interactive, cat, pasteHint }) {
    const CatIcon = cat.Icon;
    return (
        <div className={`flex flex-col items-center gap-3 px-6 text-center ${interactive ? 'pointer-events-none' : ''}`}>
            <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${cat.chip} ${cat.accent}`}>
                <CatIcon size={27} stroke={1.6} />
            </div>
            <div>
                <div className="text-[15px] font-semibold text-[#3D3833] dark:text-zinc-200">{title}</div>
                {hint ? (
                    <div className="mt-0.5 text-[12.5px] text-[#A8A099] dark:text-zinc-500">{hint}</div>
                ) : null}
                {/* Paste has no affordance of its own — nothing on screen says the
                    shortcut exists — so the empty state is where it gets named. */}
                {pasteHint ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#F7F5F3] px-2.5 py-1 text-[11.5px] font-medium text-[#6B635C] dark:bg-zinc-800 dark:text-zinc-400">
                        {pasteHint}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function PreviewProcessing({ label, progress, tone = 'solid' }) {
    return (
        <div className={`absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center ${
            // 'veil' leaves the work visible through a frosted sheet — the tool
            // keeps showing the picture it is busy with instead of a blank box.
            tone === 'veil'
                ? 'bg-white/45 backdrop-blur-[7px] dark:bg-zinc-900/45'
                : 'bg-[rgba(251,250,249,0.93)] backdrop-blur-[2px] dark:bg-zinc-900/90'
        }`}>
            <div className="h-[42px] w-[42px] animate-spin rounded-full border-[3px] border-primary-100 border-t-primary dark:border-primary-900 dark:border-t-primary" />
            <div className="text-sm font-semibold text-[#3D3833] dark:text-zinc-200">{label}</div>
            <div className="h-1.5 w-[70%] max-w-[200px] overflow-hidden rounded-full bg-primary-100 dark:bg-primary-900/40">
                <div
                    className="h-full rounded-full bg-primary transition-[width] duration-150 ease-out"
                    style={{ width: `${progress ?? 35}%` }}
                />
            </div>
        </div>
    );
}

function PreviewError({ message }) {
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F7F5F3] px-6 text-center dark:bg-zinc-800/80">
            <div className="flex h-[54px] w-[54px] items-center justify-center rounded-[15px] bg-pdf-cat-tint text-[#D33A2C] dark:bg-red-950/30">
                <IconAlertTriangle size={26} stroke={1.8} />
            </div>
            <div className="max-w-[260px] text-sm font-semibold text-[#D33A2C]">{message}</div>
        </div>
    );
}

function PreviewSuccess({
    successLabel,
    downloadLabel,
    printLabel,
    onDownload,
    onPrint,
    downloads,
    onAgain,
    againLabel,
    onEditAgain,
    editAgainLabel,
    sizeNote,
    downloadVariant = 'default',
    bundleDownload,
    cat,
    /** The sticky bar holds download / print / edit again; only the result itself shows here. */
    actionsInBar = false,
}) {
    const { t } = useTranslation();
    const CatIcon = cat.Icon;
    const multiDownloads = downloadVariant === 'compress'
        ? (downloads?.length ? downloads : null)
        : (downloads?.length > 1 ? downloads : null);
    const resolvedDownload = onDownload
        ?? (downloads?.length === 1 ? downloads[0]?.onDownload : undefined);
    // Bundle mode: a single ZIP button replaces every per-file download button.
    const useBundle = !actionsInBar && !!bundleDownload && !!multiDownloads;
    // With the bar in charge, a lone result needs no buttons of its own; one file
    // of several keeps small icons, since the bar's button fetches them all.
    const cardActions = (item) => (actionsInBar
        ? (multiDownloads.length > 1 ? item : {})
        : (useBundle ? {} : item));
    // In the default variant the ZIP button sits above the per-file rows, so the
    // shared downloadLabel ("Download ZIP") must not leak into the per-row tooltips.
    const itemDownloadLabel = bundleDownload ? t('toolPage.download') : downloadLabel;

    return (
        <div className="animate-[toolFade_0.3s_ease-out_both] flex flex-col gap-2.5">
            <div className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-[#1E9E5E]">
                <IconCheck size={16} stroke={2.2} />
                {successLabel}
            </div>
            {!multiDownloads && sizeNote ? (
                <div className="flex animate-[toolFade_0.3s_ease-out_both] items-center justify-center gap-3.5 text-[13px]">
                    {sizeNote}
                </div>
            ) : null}
            {multiDownloads && downloadVariant === 'compress' ? (
                <>
                    <div
                        className={`grid w-full gap-2.5 ${
                            multiDownloads.length === 1
                                ? 'grid-cols-1'
                                : multiDownloads.length === 2
                                    ? 'grid-cols-1 sm:grid-cols-2'
                                    : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
                        }`}
                    >
                        {multiDownloads.map((item) => (
                            <CompressResultCard
                                key={item.id || item.name}
                                name={item.name}
                                originalSize={item.originalSize}
                                compressedSize={item.compressedSize}
                                reductionPercent={item.reductionPercent}
                                downloadLabel={downloadLabel}
                                printLabel={printLabel || 'Print'}
                                onDownload={cardActions(item).onDownload}
                                onPrint={cardActions(item).onPrint}
                                compact={actionsInBar}
                                Icon={CatIcon}
                                iconClass={`${cat.chip} ${cat.accent}`}
                            />
                        ))}
                    </div>
                    {useBundle ? (
                        <button
                            type="button"
                            onClick={bundleDownload.onClick}
                            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-semibold text-white transition-colors hover:bg-primary/90"
                        >
                            <IconDownload size={18} stroke={1.9} />
                            {bundleDownload.label}
                        </button>
                    ) : null}
                </>
            ) : multiDownloads ? (
                <>
                {bundleDownload && !actionsInBar ? (
                    <button
                        type="button"
                        onClick={bundleDownload.onClick}
                        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                        <IconDownload size={18} stroke={1.9} />
                        {bundleDownload.label}
                    </button>
                ) : null}
                <div className={`grid gap-2.5 ${multiDownloads.length >= 3 ? 'grid-cols-1 sm:grid-cols-3' : multiDownloads.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                    {multiDownloads.map((item) => (
                        <div
                            key={item.id || item.name}
                            className="flex items-center gap-2.5 rounded-xl border border-[#ECE8E4] bg-white p-2.5 transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900"
                        >
                            <div className={`flex h-10 w-10 flex-none items-center justify-center rounded-lg ${cat.chip} ${cat.accent}`}>
                                <CatIcon size={20} stroke={1.7} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-100" title={item.name}>
                                    {item.name}
                                </p>
                                {item.meta ? (
                                    <p className="text-[11px] font-medium text-[#A8A099] dark:text-zinc-500">{item.meta}</p>
                                ) : null}
                            </div>
                            <Hint label={itemDownloadLabel}>
                                <button
                                    type="button"
                                    onClick={item.onDownload}
                                    aria-label={itemDownloadLabel}
                                    className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary text-white transition-colors hover:bg-primary/90"
                                >
                                    <IconDownload size={16} stroke={1.9} />
                                </button>
                            </Hint>
                        </div>
                    ))}
                </div>
                </>
            ) : resolvedDownload && !actionsInBar ? (
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={resolvedDownload}
                        className="inline-flex h-12 min-w-[140px] flex-1 items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                        <IconDownload size={18} stroke={1.9} />
                        {downloadLabel}
                    </button>
                    {onPrint ? (
                        <button
                            type="button"
                            onClick={onPrint}
                            className="inline-flex h-12 flex-none items-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconPrinter size={16} stroke={1.9} />
                            {printLabel || t('toolPage.print')}
                        </button>
                    ) : null}
                    {onEditAgain ? (
                        <button
                            type="button"
                            onClick={onEditAgain}
                            className="inline-flex h-12 flex-none items-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconPencil size={16} stroke={1.9} />
                            {editAgainLabel || t('toolPage.editAgain')}
                        </button>
                    ) : null}
                    {onAgain ? (
                        <button
                            type="button"
                            onClick={onAgain}
                            className="inline-flex h-12 flex-none items-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconRefresh size={16} stroke={1.9} />
                            {againLabel || t('toolPage.again')}
                        </button>
                    ) : null}
                </div>
            ) : null}
            {!actionsInBar && (multiDownloads || resolvedDownload) && downloadVariant === 'compress' && (onEditAgain || onAgain) ? (
                <div className="flex flex-wrap gap-2">
                    {onEditAgain ? (
                        <button
                            type="button"
                            onClick={onEditAgain}
                            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconPencil size={16} stroke={1.9} />
                            {editAgainLabel || t('toolPage.editAgain')}
                        </button>
                    ) : null}
                    {onAgain ? (
                        <button
                            type="button"
                            onClick={onAgain}
                            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconRefresh size={16} stroke={1.9} />
                            {againLabel || t('toolPage.again')}
                        </button>
                    ) : null}
                </div>
            ) : null}
            {!actionsInBar && multiDownloads && downloadVariant !== 'compress' && (onEditAgain || onAgain) ? (
                <div className="flex flex-wrap gap-2">
                    {onEditAgain ? (
                        <button
                            type="button"
                            onClick={onEditAgain}
                            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconPencil size={16} stroke={1.9} />
                            {editAgainLabel || t('toolPage.editAgain')}
                        </button>
                    ) : null}
                    {onAgain ? (
                        <button
                            type="button"
                            onClick={onAgain}
                            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconRefresh size={16} stroke={1.9} />
                            {againLabel || t('toolPage.again')}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

/**
 * Right-hand preview panel with idle / processing / error overlays and success actions.
 */
export default function ToolPreviewPanel({
    title,
    status,
    statusLabel,
    idleTitle,
    idleHint,
    processingLabel,
    progress,
    error,
    successLabel,
    downloadLabel,
    onPrint,
    onDownload,
    downloads,
    onAgain,
    againLabel,
    onEditAgain,
    editAgainLabel,
    readyHint,
    sizeNote,
    downloadVariant,
    bundleDownload,
    printLabel,
    children,
    showIdle = true,
    tall = true,
    fill = false,
    /**
     * With `fill`, the smallest the work area may shrink to (a Tailwind class,
     * e.g. 'min-h-[520px]'). Without it a filled panel can collapse to its
     * content when the column beside it is short.
     */
    fillMinHeightClass,
    flushContent = false,
    /**
     * Canvas behind the preview content. 'ash' is the default tinted work area;
     * 'plain' keeps it white, for tools whose content is already cards on a
     * surface of their own and would otherwise sit in a grey box.
     */
    canvasTone = 'ash',
    /**
     * 'veil' keeps the content visible (frosted) behind the processing state;
     * 'none' renders no overlay at all, for tools that show progress on the
     * individual items instead of over the whole panel.
     */
    processingTone = 'solid',
    pdfPreview = false,
    onIdleClick,
    onIdleDrop,
    onIdleDragOver,
    onIdleDragLeave,
    idleDragging = false,
    /** Tools that accept a pasted picture say so in their empty state. */
    showPasteHint = false,
    category,
    /**
     * A control that belongs to the preview itself rather than to the page —
     * the zoom buttons on the printed-form tools, say. Sits in the panel header,
     * before the status badge.
     */
    headerAction,
}) {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    // The panel is shared by every tool category, so the file icon and its tint
    // follow the route (`/tools/image/...` → photo icon) unless a page overrides it.
    const cat = catFromPath(category ? `/tools/${category}` : pathname);
    const isIdle = status === 'empty' && showIdle;
    const isReady = status === 'ready';
    const isProcessing = status === 'processing';
    const isSuccess = status === 'success';
    const isError = status === 'error';
    const idleInteractive = isIdle && !!onIdleClick;
    const showChildren = !isIdle && !isError;
    const stretchPanel = fill || tall;
    const previewViewportClass = fill
        ? `flex-1 ${fillMinHeightClass || 'min-h-0'}`
        : pdfPreview
            ? 'h-[min(920px,90vh)] max-h-[min(920px,90vh)]'
            : tall
                ? 'h-[min(640px,68vh)] max-h-[min(640px,68vh)]'
                : 'min-h-[320px]';
    // Finished-result buttons move to the page's sticky bar when it offers one.
    // Start over is not repeated there: রিসেট already does it.
    const resultBar = useContext(ToolResultBarContext);
    const hasResult = isSuccess && !!(onDownload || downloads?.length);
    const actionsInBar = !!resultBar && hasResult;
    const isMulti = (downloads?.length || 0) > 1;
    const singleItem = !isMulti && downloads?.length === 1 ? downloads[0] : null;
    const barHandlers = useRef({});
    barHandlers.current = {
        download: isMulti
            ? (bundleDownload?.onClick ?? (async () => {
                // No ZIP from this tool: fetch the files one after another.
                for (const item of downloads) {
                    item.onDownload?.();
                    await new Promise((resolve) => { setTimeout(resolve, 400); });
                }
            }))
            : (onDownload ?? singleItem?.onDownload),
        print: onPrint ?? singleItem?.onPrint,
        editAgain: onEditAgain,
    };
    // Plain strings only, so a page re-rendering with fresh callbacks does not
    // re-register the same buttons.
    const barSignature = actionsInBar
        ? JSON.stringify([
            isMulti ? (bundleDownload?.label || t('toolPage.downloadAll')) : (downloadLabel || t('toolPage.download')),
            (onPrint || singleItem?.onPrint) ? (printLabel || t('toolPage.print')) : null,
            onEditAgain ? (editAgainLabel || t('toolPage.editAgain')) : null,
        ])
        : null;

    useEffect(() => {
        if (!resultBar || !barSignature) return undefined;
        const [downloadText, printText, editText] = JSON.parse(barSignature);
        resultBar.setResultActions({
            primary: {
                label: downloadText,
                icon: IconDownload,
                onClick: () => barHandlers.current.download?.(),
            },
            secondary: [
                editText && {
                    key: 'edit-again',
                    label: editText,
                    icon: IconPencil,
                    onClick: () => barHandlers.current.editAgain?.(),
                },
                printText && {
                    key: 'print',
                    label: printText,
                    icon: IconPrinter,
                    onClick: () => barHandlers.current.print?.(),
                },
            ].filter(Boolean),
        });
        return () => resultBar.setResultActions(null);
    }, [resultBar, barSignature]);

    const flushViewportClass = fill
        ? `flex min-h-0 flex-1 flex-col${tall ? ' min-h-[min(640px,68vh)]' : ''}`
        : tall
            ? 'flex min-h-[min(640px,68vh)] flex-1 flex-col'
            : '';

    const handleIdleKeyDown = (e) => {
        if (!idleInteractive) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onIdleClick();
        }
    };

    return (
        <div className={`flex flex-col gap-3.5 rounded-[20px] border border-[#ECE8E4] bg-white p-[18px] shadow-[0_8px_24px_rgba(28,24,21,0.06)] dark:border-zinc-700 dark:bg-zinc-900 ${stretchPanel ? 'h-full min-h-0 flex-1 overflow-hidden' : ''}`}>
            <div className="flex shrink-0 items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">{title}</span>
                <div className="flex min-w-0 items-center gap-2">
                    {headerAction}
                    <PreviewStatusBadge status={status} label={statusLabel} />
                </div>
            </div>

            <div
                role={idleInteractive ? 'button' : undefined}
                tabIndex={idleInteractive ? 0 : undefined}
                onClick={idleInteractive ? onIdleClick : undefined}
                onKeyDown={idleInteractive ? handleIdleKeyDown : undefined}
                onDragOver={idleInteractive && onIdleDragOver ? onIdleDragOver : undefined}
                onDragLeave={idleInteractive && onIdleDragLeave ? onIdleDragLeave : undefined}
                onDrop={idleInteractive && onIdleDrop ? onIdleDrop : undefined}
                className={flushContent
                    ? `relative w-full min-h-0 ${flushViewportClass} ${
                        isIdle ? 'items-center justify-center' : ''
                    }`
                    : `relative flex w-full flex-col rounded-2xl p-3 ${
                        // 'plain' only strips the work-area look once there is
                        // content; while empty the tint and its outline are what
                        // read as a drop zone.
                        canvasTone === 'plain' && !isIdle
                            ? 'bg-white dark:bg-zinc-900'
                            : 'bg-[#F7F5F3] shadow-[inset_0_0_0_1px_#ECE8E4] dark:bg-zinc-800/60 dark:shadow-[inset_0_0_0_1px_theme(colors.zinc.700)]'
                    } ${previewViewportClass} ${
                        fill ? (fillMinHeightClass || 'min-h-0') : 'shrink-0'
                    } ${
                        showChildren && children
                            ? 'items-stretch justify-start overflow-hidden'
                            : 'items-center justify-center overflow-hidden'
                    } ${
                        idleInteractive
                            ? `cursor-pointer transition-colors ${
                                idleDragging
                                    ? 'bg-pdf-cat-tint shadow-[inset_0_0_0_2px_theme(colors.pdf-cat.solid)] dark:bg-orange-950/25'
                                    : 'hover:bg-[#F0ECE7] hover:shadow-[inset_0_0_0_1px_theme(colors.pdf-cat.solid/30)] dark:hover:bg-zinc-800'
                            }`
                            : ''
                    }`}
                >
                {showChildren ? (
                    <div className={flushContent
                        ? `w-full min-h-0 ${flushViewportClass}`
                        : 'relative h-full min-h-0 w-full overflow-hidden'
                    }
                    >
                        {children}
                    </div>
                ) : null}

                {isIdle ? (
                    <PreviewIdle
                        title={idleTitle || t('toolPage.previewEmpty')}
                        hint={idleHint || t('toolPage.previewEmptyHint')}
                        interactive={idleInteractive}
                        cat={cat}
                        pasteHint={showPasteHint ? t('toolPage.pasteHint') : null}
                    />
                ) : null}

                {isProcessing && processingTone !== 'none' ? (
                    <PreviewProcessing
                        label={processingLabel || t('toolPage.processing')}
                        progress={progress}
                        tone={processingTone}
                    />
                ) : null}

                {isError && error ? <PreviewError message={error} /> : null}
            </div>

            {isReady && readyHint ? (
                <div className="shrink-0 text-center text-[12.5px] text-[#6B635C] dark:text-zinc-400">
                    {readyHint}
                </div>
            ) : null}

            {isSuccess && (onDownload || downloads?.length) ? (
                <PreviewSuccess
                    successLabel={successLabel}
                    downloadLabel={downloadLabel}
                    printLabel={printLabel}
                    onDownload={onDownload}
                    onPrint={onPrint}
                    downloads={downloads}
                    onAgain={onAgain}
                    againLabel={againLabel}
                    onEditAgain={onEditAgain}
                    editAgainLabel={editAgainLabel}
                    sizeNote={sizeNote}
                    downloadVariant={downloadVariant}
                    bundleDownload={bundleDownload}
                    cat={cat}
                    actionsInBar={actionsInBar}
                />
            ) : null}
        </div>
    );
}
